"use client";

import { useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { StarIcon } from "./icons";

export const RATING_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export interface RatingStarsProps {
  /** The selected rating (1-10), or null for none. */
  value: number | null;
  onChange: (rating: number) => void;
  /** Accessible name of the group, e.g. "Your rating for Face/Off". */
  label: string;
  /** Accessible name of each star, e.g. "Rate 7 out of 10". */
  starLabel: (rating: number) => string;
  /** Called while the pointer previews a rating (null when it leaves). */
  onPreviewChange?: (rating: number | null) => void;
  /** Focus the selected (or first) star when mounted. */
  autoFocus?: boolean;
  className?: string;
}

/**
 * A 1-10 star rating input, built as an ARIA radio group: Tab focuses the
 * selected star, arrow keys / Home / End change the rating, and hovering
 * previews a value before clicking.
 */
export function RatingStars({
  value,
  onChange,
  label,
  starLabel,
  onPreviewChange,
  autoFocus = false,
  className,
}: RatingStarsProps) {
  const [preview, setPreview] = useState<number | null>(null);
  const stars = useRef<Array<HTMLButtonElement | null>>([]);
  const shown = preview ?? value ?? 0;
  const tabStop = value ?? 1;

  const updatePreview = (rating: number | null) => {
    setPreview(rating);
    onPreviewChange?.(rating);
  };

  const select = (rating: number, moveFocus: boolean) => {
    onChange(rating);
    if (moveFocus) stars.current[rating - 1]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    rating: number,
  ) => {
    let next: number;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowUp":
        next = Math.min(10, rating + 1);
        break;
      case "ArrowLeft":
      case "ArrowDown":
        next = Math.max(1, rating - 1);
        break;
      case "Home":
        next = 1;
        break;
      case "End":
        next = 10;
        break;
      default:
        return;
    }
    event.preventDefault();
    select(next, true);
  };

  return (
    // The wrapper only ends the hover preview; the stars are the controls.
    <div
      className={cn("w-full max-w-[17rem] sm:max-w-[24rem]", className)}
      onMouseLeave={() => updatePreview(null)}
    >
      {/* Phones: two rows of five thumb-sized stars; wider screens: one row. */}
      <div
        aria-label={label}
        className="grid grid-cols-5 gap-1 sm:grid-cols-10 sm:gap-0.5"
        role="radiogroup"
      >
        {RATING_VALUES.map((rating) => {
          const lit = rating <= shown;

          return (
            <button
              key={rating}
              ref={(element) => {
                stars.current[rating - 1] = element;
              }}
              aria-checked={value === rating}
              aria-label={starLabel(rating)}
              // The rating dialog opens to pick a rating: start on the stars.
              autoFocus={autoFocus && rating === tabStop}
              className={cn(
                "flex aspect-square min-h-11 min-w-11 items-center justify-center rounded-md p-1 transition-transform sm:min-h-9 sm:min-w-9 sm:p-0.5",
                "hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus motion-reduce:transition-none motion-reduce:hover:scale-100",
                lit ? "text-warning" : "text-default-300 dark:text-default-400",
              )}
              data-lit={lit}
              role="radio"
              tabIndex={rating === tabStop ? 0 : -1}
              type="button"
              onClick={() => select(rating, false)}
              onKeyDown={(event) => handleKeyDown(event, rating)}
              onMouseEnter={() => updatePreview(rating)}
            >
              <StarIcon
                className="h-full w-full"
                filled={lit}
                strokeWidth={1.5}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
